import React, { useEffect, useState } from 'react'
import { fetchAdminDetails, updateAdminProfile } from '../redux/AdminSlice'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
    Lock,
    User,
    Briefcase,
    MapPin,
    FileText,
    Check,
    X,
    Edit
} from 'lucide-react'
import { formatDate } from '../utils/Config'

// Mapping of role names to readable labels and icons
const ROLE_MAPPING = {
    users: { label: 'User Management', icon: User },
    reported_posts: { label: 'Content/Post Moderation', icon: FileText },
    jobs: { label: 'Job Listings', icon: Briefcase },
    user_geolocation: { label: 'User Tracking', icon: MapPin },
    applicants: { label: 'Applicant Tracking', icon: Lock }
}

const AdminDetail = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        status: '',
        roles: []
    })

    // Fetch admin details
    useEffect(() => {
        if (id) {
            dispatch(fetchAdminDetails(id))
        }
    }, [dispatch, id])

    // Get admin details from Redux store
    const { selectedAdmin, loading, error } = useSelector((state) => state.admins)

    // Populate form data when admin is loaded
    useEffect(() => {
        if (selectedAdmin) {
            setFormData({
                first_name: selectedAdmin?.first_name,
                last_name: selectedAdmin?.last_name,
                email: selectedAdmin?.email,
                status: selectedAdmin?.status,
                roles: JSON.parse(selectedAdmin?.roles || '[]')
            })
        }
    }, [selectedAdmin])

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    // Handle status toggle
    const handleStatusToggle = () => {
        setFormData(prev => ({
            ...prev,
            status: prev.status === 'active' ? 'inactive' : 'active'
        }))
    }

    // Handle role toggle
    const handleRoleToggle = (role) => {
        setFormData(prev => ({
            ...prev,
            roles: prev.roles.includes(role)
                ? prev.roles.filter(r => r !== role)
                : [...prev.roles, role]
        }))
    }



    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault()
        dispatch(updateAdminProfile({
            adminId: selectedAdmin?.id,
            data: {
                ...formData,
                roles: JSON.stringify(formData.roles)
            }
        }))
        setIsEditModalOpen(false)
    }

    // Render loading state
    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
            </div>
        )
    }

    // Render error state
    if (error) {
        return (
            <div className="container mx-auto p-6 text-red-500">
                <p>Error loading admin details: {error}</p>
            </div>
        )
    }

    // Render admin details
    return (
        <div className="container mx-auto p-6">
            <div className="mb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="hover:underline"
                >
                    Go back
                </button>
            </div>

            <div className="rounded-lg overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 flex justify-between items-center border-b">
                    <h2 className="text-2xl font-bold text-gray-800">Admin Profile</h2>
                    <button
                        onClick={() => setIsEditModalOpen(true)}
                        className="w-fit sm:w-fit font-lufga text-white bg-[#1330C5] hover:opacity-90 focus:outline-none rounded-md shadow-md text-base px-4 py-2 sm:px-10 sm:py-2 text-center transition-colors flex items-center"
                    >
                        <Edit className="mr-2 w-5 h-5" />
                        Edit Profile
                    </button>
                </div>

                {/* Profile Details */}
                <div className="p-6 grid md:grid-cols-2 gap-6">
                    {/* Personal Information */}
                    <div>
                        <h3 className="text-lg font-semibold mb-4 pb-2 border-b">Personal Information</h3>
                        <div className="space-y-3">
                            <div className="flex items-center">
                                <span className="font-medium mr-2">Name:</span>
                                <span>{`${selectedAdmin?.first_name} ${selectedAdmin?.last_name}`}</span>
                            </div>
                            <div className="flex items-center">
                                <span className="font-medium mr-2">Email:</span>
                                <span>{selectedAdmin?.email}</span>
                            </div>
                            <div className="flex items-center">
                                <span className="font-medium mr-2">Status:</span>
                                <span
                                    className={`px-3 py-1 rounded-full text-sm ${selectedAdmin?.status === 'active'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}
                                >
                                    {selectedAdmin?.status}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Roles */}
                    {/* <div>
                        <h3 className="text-lg font-semibold mb-4 pb-2 border-b">Assigned Roles</h3>
                        <div className="space-y-2">
                            {Object.entries(ROLE_MAPPING).map(([role, { label, icon: Icon }]) => (
                                <div
                                    key={role}
                                    className={`flex items-center p-2 rounded-md transition-colors ${formData.roles.includes(role)
                                            ? 'bg-green-50 text-green-700'
                                            : 'bg-gray-50 text-gray-500'
                                        }`}
                                >
                                    <Icon className="mr-3 w-5 h-5" />
                                    <span className="flex-grow">{label}</span>
                                    {formData.roles.includes(role) ? (
                                        <Check className="text-green-600 w-5 h-5" />
                                    ) : (
                                        <X className="text-gray-400 w-5 h-5" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div> */}

                    {/* Account Details */}
                    <div className="md:col-span-2">
                        <h3 className="text-lg font-semibold mb-4 pb-2 border-b">Account Details</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <span className="font-medium mr-2">Created At:</span>
                                <span>{formatDate(selectedAdmin?.created_at)}</span>
                            </div>
                            <div>
                                <span className="font-medium mr-2">UUID:</span>
                                <span className="text-sm text-gray-600">{selectedAdmin?.uuid}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Profile Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h2 className="text-xl font-bold mb-4">Edit Profile</h2>
                        <form onSubmit={handleSubmit}>
                            {/* Personal Information Inputs */}
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-2">First Name</label>
                                <input
                                    type="text"
                                    name="first_name"
                                    value={formData.first_name}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-2">Last Name</label>
                                <input
                                    type="text"
                                    name="last_name"
                                    value={formData.last_name}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-2">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            {/* Status Toggle */}
                            <div className="mb-4 flex items-center justify-between">
                                <label className="block text-gray-700">Status</label>
                                <div
                                    onClick={handleStatusToggle}
                                    className={`relative w-14 h-7 rounded-full cursor-pointer transition-colors duration-300 ${formData.status === 'active'
                                            ? 'bg-green-500'
                                            : 'bg-red-500'
                                        }`}
                                >
                                    <div
                                        className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${formData.status === 'active'
                                                ? 'translate-x-8'
                                                : 'translate-x-1'
                                            }`}
                                    ></div>
                                </div>
                            </div>

                            {/* Role Management */}
                            {/* <div className="mb-4">
                                <h3 className="text-lg font-semibold mb-3">Manage Roles</h3>
                                {Object.entries(ROLE_MAPPING).map(([role, { label, icon: Icon }]) => (
                                    <div
                                        key={role}
                                        className="flex items-center mb-2 p-2 rounded-md hover:bg-gray-100 cursor-pointer"
                                        onClick={() => handleRoleToggle(role)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={formData.roles.includes(role)}
                                            onChange={() => handleRoleToggle(role)}
                                            className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                        />
                                        <Icon className="mr-3 w-5 h-5 text-gray-500" />
                                        <span>{label}</span>
                                    </div>
                                ))}
                            </div> */}

                            {/* Action Buttons */}
                            <div className="flex justify-end space-x-4">
                                <button
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="w-fit sm:w-fit font-lufga text-white bg-[#1330C5] hover:opacity-90 focus:outline-none rounded-md shadow-md text-base px-4 py-2 sm:px-10 sm:py-2 text-center transition-colors "
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminDetail